import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';
import { qk } from './queries';

/** Tecto por envio, igual ao da API — um relógio parado não injecta horas. */
const MAX_SEGUNDOS_POR_ENVIO = 3 * 3600;

/** A partir daqui considera-se que a pessoa saiu da frente do ecrã. */
const INATIVIDADE_SEGUNDOS = 120;

/** De quanto em quanto tempo o acumulado sobe para o servidor. */
const INTERVALO_ENVIO_SEGUNDOS = 60;

export type SessionType = 'teoria' | 'questoes' | 'revisao';

export interface FocusTimer {
  /** Segundos contados nesta sessão de estudo, incluindo o que já foi enviado. */
  segundos: number;
  ativo: boolean;
  /** Envia o acumulado pendente; chamada ao sair da página. */
  descarregar: () => void;
}

/**
 * Cronómetro do tempo focado.
 *
 * O Flask media isto com um `setInterval` no navegador que continuava a somar
 * com o separador em segundo plano e com a pessoa fora do computador — daí os
 * dias de "14 horas de estudo" nas estatísticas antigas. Aqui a contagem pára
 * quando o separador fica oculto e quando não há interacção há dois minutos, e
 * o envio é feito em blocos com `sendBeacon` na saída, para não se perder o
 * último minuto quando a pessoa fecha o separador.
 */
export function useFocusTimer(
  sessionType: SessionType,
  habilitado = true,
): FocusTimer {
  const [segundos, setSegundos] = useState(0);
  const [ativo, setAtivo] = useState(true);

  const pendentes = useRef(0);
  const ultimaInteracao = useRef(Date.now());
  const queryClient = useQueryClient();

  const enviar = useCallback(
    (usarBeacon: boolean) => {
      const total = Math.min(Math.floor(pendentes.current), MAX_SEGUNDOS_POR_ENVIO);
      if (total < 5) return;
      pendentes.current -= total;

      const payload = JSON.stringify({ seconds: total, sessionType });

      if (usarBeacon && typeof navigator.sendBeacon === 'function') {
        // `fetch` durante o unload é cancelado; o beacon é entregue.
        navigator.sendBeacon(
          '/api/estatisticas/tempo',
          new Blob([payload], { type: 'application/json' }),
        );
        return;
      }

      void api('/estatisticas/tempo', {
        method: 'POST',
        body: { seconds: total, sessionType },
      })
        .then(() => {
          void queryClient.invalidateQueries({ queryKey: qk.tempoHoje });
        })
        .catch(() => {
          // Perder um minuto de contagem é preferível a interromper o estudo
          // com um erro; o bloco seguinte volta a tentar.
        });
    },
    [sessionType, queryClient],
  );

  useEffect(() => {
    if (!habilitado) return;

    const registarInteracao = () => {
      ultimaInteracao.current = Date.now();
    };

    const eventos = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;
    for (const evento of eventos) {
      window.addEventListener(evento, registarInteracao, { passive: true });
    }

    const tick = window.setInterval(() => {
      const oculto = document.hidden;
      const inativo =
        (Date.now() - ultimaInteracao.current) / 1000 > INATIVIDADE_SEGUNDOS;

      if (oculto || inativo) {
        setAtivo(false);
        return;
      }

      setAtivo(true);
      pendentes.current += 1;
      setSegundos((s) => s + 1);

      if (pendentes.current >= INTERVALO_ENVIO_SEGUNDOS) enviar(false);
    }, 1000);

    const aoEsconder = () => {
      if (document.hidden) enviar(true);
    };
    const aoSair = () => enviar(true);
    document.addEventListener('visibilitychange', aoEsconder);
    window.addEventListener('pagehide', aoSair);

    return () => {
      window.clearInterval(tick);
      document.removeEventListener('visibilitychange', aoEsconder);
      window.removeEventListener('pagehide', aoSair);
      for (const evento of eventos) {
        window.removeEventListener(evento, registarInteracao);
      }
      enviar(true);
    };
  }, [habilitado, enviar]);

  const descarregar = useCallback(() => enviar(false), [enviar]);

  return { segundos, ativo, descarregar };
}
